import { HttpRequest, HttpHandler, HttpInterceptor, HttpErrorResponse, HttpClient, HttpEvent, HttpHeaders } from "@angular/common/http";
import { Injectable, Injector } from "@angular/core";
import { Router } from "@angular/router";
import { BehaviorSubject } from "rxjs";
import { Observable, throwError as observableThrowError } from 'rxjs';
import { JwtHelperService } from "@auth0/angular-jwt";
import { ServicioToken } from "../../decodeToken";
import { catchError, filter, switchMap, take } from "rxjs/operators";
import { ServicioAuth } from "./auth.service";
import { environment } from "@environments/environment";
import * as myGlobals from "app/modelos/globales.modelo";

@Injectable()
export class RequestInterceptorService implements HttpInterceptor {
  tokenSubject: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  isRefreshingToken = false;

  constructor(
    public router: Router,
    private injector: Injector,
    private jwt: JwtHelperService,
    private servicioToken: ServicioToken,
  ) {}

  addToken(req: HttpRequest<any>, token: string): HttpRequest<any> {
    return req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    // Evitar bucles: no interceptar refresh/login o si ya está marcado con 'skip'

    const skipIntercept =
      req.url.includes("/refresh") ||
      req.url.includes("/login") ||
      req.headers.has("skip");

    if (skipIntercept) {
      return next.handle(req.clone({ headers: req.headers.delete("skip") }));
    }

    const accessToken = localStorage.getItem("AccessToken");
    if (
      (accessToken && !this.jwt.isTokenExpired(accessToken)) ||
      (!this.servicioToken.isTokenExpired() &&
        this.servicioToken.getIdTercero()) ||
      this.servicioToken.isAdmin()
    ) {
      const token = this.isRefreshingToken
        ? localStorage.getItem("RefreshToken")
        : accessToken;
      const authReq = this.addToken(req.clone({}), token);

      return next.handle(authReq).pipe(
        catchError((error) => {
          if (req.url.indexOf("/refresh") !== -1) {
            return this.send500Page();
          }

          if (error instanceof HttpErrorResponse) {
            switch (error.status) {
              case 400:
                return this.handle401Error(req, next);
              case 401:
                return this.handle401Error(req, next);
              case 403:
                return this.handle401Error(req, next);
              case 404:
                return observableThrowError(() => error);
              case 501:
                return this.handle501Error(error.error.message);
              case 523:
                const reader = new FileReader();
                reader.readAsText(error.error);
                reader.onload = (r) => {
                  this.handle523Error(String(r.target.result));
                };
                return observableThrowError(() => new Error(""));
              case 524:
                return this.handle524Error(error.error);
              case 534:
                return this.handle534Error(error.error.message);
              default:
                return this.handle401Error(req, next);
            }
          } else {
            return observableThrowError(() => error);
          }
        }),
      );
    } else {
      const servicioAuth = this.injector.get(ServicioAuth);
      return servicioAuth.getPublicToken().pipe(
        switchMap((data) => {
          if (data.success) {
            localStorage.setItem("AccessToken", data.accesstoken.token);
            localStorage.setItem("RefreshToken", data.refreshtoken.token);

            servicioAuth.getBasicInfo().subscribe((result) => {
              const nombreCliente =
                result?.aspnetacceso?.nombre_cliente || "Cita Previa";
              localStorage.setItem("NombreCliente", nombreCliente);
              myGlobals.setLocalStore(
                myGlobals.eLocalStore.ATM_CONNECT,
                result.parametrosaplicacioncliente.ATM_CONNECT,
              );
              myGlobals.setLocalStore(
                myGlobals.eLocalStore.PublicUrl,
                result.parametrosaplicacioncliente.PublicUrl,
              );
              myGlobals.setLocalStore(
                myGlobals.eLocalStore.AtmConnectConnection,
                result.parametrosplataforma.AtmConnectConnection,
              );
            });

            this.tokenSubject.next(data.accesstoken.token);

            const authReq = this.addToken(
              req.clone({
                headers: req.headers.set("skip", "true"),
              }),
              data.accesstoken.token,
            );

            return next.handle(authReq);
          } else {
            this.handle501Error(data.message);
            return this.send404Page();
          }
        }),
        catchError(() => this.logoutUser()),
      );
    }
  }

  handle401Error(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    if (!this.isRefreshingToken) {
      this.isRefreshingToken = true;
      this.tokenSubject.next(null);

      const servicioAuth = this.injector.get(ServicioAuth);

      if (
        !this.servicioToken.isTokenExpired() &&
        this.servicioToken.getIdTercero()
      ) {
        return servicioAuth.getPublicToken().pipe(
          switchMap((data) => {
            localStorage.setItem("AccessToken", data.accesstoken.token);
            this.tokenSubject.next(data.accesstoken.token);
            this.isRefreshingToken = false;

            const newReq = this.addToken(
              req.clone({
                headers: req.headers.set("skip", "true"),
              }),
              data.accesstoken.token,
            );

            return next.handle(newReq);
          }),
          catchError(() => {
            this.isRefreshingToken = false;
            return this.logoutUser();
          }),
        );
      } else {
        const http = this.injector.get(HttpClient);
        const headers = new HttpHeaders({
          Authorization: `Bearer ${localStorage.getItem("RefreshToken")}`,
        });
        return http
          .get<any>(environment.backends.URL_API_ROOT + "/api/auth/refresh/", {
            headers,
          })
          .pipe(
            switchMap((data) => {
              localStorage.setItem("AccessToken", data.accesstoken.token);
              localStorage.setItem("RefreshToken", data.refreshtoken.token);
              this.tokenSubject.next(data.accesstoken.token);
              this.isRefreshingToken = false;

              const newReq = this.addToken(
                req.clone({
                  headers: req.headers.set("skip", "true"),
                }),
                data.accesstoken.token,
              );

              return next.handle(newReq);
            }),
            catchError(() => {
              this.isRefreshingToken = false;
              return this.send500Page();
            }),
          );
      }
    } else {
      return this.tokenSubject.pipe(
        filter((token) => token != null),
        take(1),
        switchMap((token) => {
          const newReq = this.addToken(
            req.clone({
              headers: req.headers.set("skip", "true"),
            }),
            token,
          );
          return next.handle(newReq);
        }),
      );
    }
  }

  logoutUser(): Observable<never> {
    this.router.navigate(["/500"]);
    localStorage.removeItem("AccessToken");
    localStorage.removeItem("RefreshToken");
    return observableThrowError(() => new Error("Sesión finalizada"));
  }

  send500Page(): Observable<never> {
    this.router.navigate(["/500"]);
    return observableThrowError(() => new Error("Error interno del servidor"));
  }

  send404Page(): Observable<never> {
    this.router.navigate(["/404"]);
    return observableThrowError(() => new Error("Recurso no encontrado"));
  }

  handle501Error(mensaje: string): Observable<never> {
    console.warn("Error 501:", mensaje);
    return observableThrowError(() => new Error(mensaje));
  }

  handle523Error(mensaje: string): Observable<never> {
    console.warn("Error 523:", mensaje);
    return observableThrowError(() => new Error(mensaje));
  }

  handle524Error(mensaje: string): Observable<never> {
    console.warn("Error 524:", mensaje);
    return observableThrowError(() => new Error(mensaje));
  }

  handle534Error(mensaje: string): Observable<never> {
    console.warn("Error 534:", mensaje);
    return this.logoutUser();
  }
}
